import { ArrowDownward, ArrowUpward, Delete, Edit, Public } from '@mui/icons-material';
import { Box, FormControl, IconButton, InputLabel, MenuItem, Paper, Tooltip, Typography } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTimeFormat, getLocalDateTime } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { ServerSideDataTable } from 'src/common/components/dataTable/serverSide';
import { DatatableColumn } from 'src/common/components/dataTable/types';
import { WO2Select } from 'src/common';
import { QueryField } from 'src/store';
import { Props } from '../../container';
import { ReportingParametersDeleteModal } from './ReportingParametersDeleteModal';
import { GetReportParametersViewModel, ReportParametersKind, ReportParametersKindName } from '../../store/types';

export const ReportingParametersTable = (props: Props): JSX.Element => {
  const history = useHistory();

  const [pagedRequest, setPagedRequest] = useState({
    pageNumber: 1,
    pageSize: 25,
    queryFields: [] as QueryField[],
  });

  const [kind, setKind] = useState<string>('C');
  const [clickedRow, setClickedRow] = useState<GetReportParametersViewModel | null>(null);
  const [clickedState, setClickedState] = useState<'delete' | null>(null);

  const fetchData = () => {
    props.fetchReportingParameters({
      includeSharedAfsl: !props.hasClientEditAdminOnlyPermission,
      kind,
      page: pagedRequest.pageNumber - 1,
      pageSize: pagedRequest.pageSize,
      queryFields: pagedRequest.queryFields,
    });
  };

  useEffect(() => {
    fetchData();
  }, [pagedRequest, kind]);

  const closeModal = () => {
    setClickedRow(null);
    setClickedState(null);
  };

  const tableOptions: MUIDataTableOptions = {
    filter: true,
    search: false,
    viewColumns: true,
    download: false,
    print: false,
    selectableRows: 'none',
    selectableRowsHideCheckboxes: true,
    pagination: true,
    serverSide: true,
    customToolbar: () => (
      <Box display="inline-flex" alignItems="center" gap={1} mt={3}>
        <FormControl size="small">
          <InputLabel id="kind-id">Kind</InputLabel>
          <WO2Select
            labelId="kind-id"
            label="Kind"
            onChange={(e) => {
              setKind(e.target.value as string);
              setPagedRequest({ ...pagedRequest, pageNumber: 1 });
            }}
            value={kind}
          >
            {Object.entries(ReportParametersKindName).map(([key, value]) => (
              <MenuItem key={key} value={key as ReportParametersKind}>
                {value}
              </MenuItem>
            ))}
          </WO2Select>
        </FormControl>
        <WO2Button variant="contained" onClick={() => history.push('/parameters/create')}>
          Create
        </WO2Button>
      </Box>
    ),
  };

  const columns: DatatableColumn[] = useMemo(
    () => [
      {
        name: 'name',
        label: 'Name',
        options: {
          sort: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.bulk.reportingParametersList?.results[dataIndex];
            if (!row) return '';
            return (
              <Box display="flex" alignItems="center" gap={1}>
                {row.isSystemShared && <Public color="primary" />}
                <Typography>{row.name}</Typography>
              </Box>
            );
          },
        },
      },
      {
        name: 'createdByName',
        label: 'Created By',
        options: {
          sort: false,
          filter: false,
        },
      },
      {
        name: 'afslName',
        label: 'Shared with AFSL',
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.bulk.reportingParametersList?.results[dataIndex];
            if (!row) return '';
            return row.visibleToAfsl ? row.afslName : '';
          },
        },
      },
      {
        name: 'kind',
        label: 'Kind',
        options: {
          sort: true,
          filter: false,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.bulk.reportingParametersList?.results[dataIndex];
            if (!row) return '';
            return row.kind ? ReportParametersKindName[row.kind as ReportParametersKind] : 'Unknown';
          },
        },
      },
      {
        name: 'createdOn',
        label: 'Created On',
        options: {
          sort: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.bulk.reportingParametersList?.results[dataIndex];
            if (!row) return '';
            return getLocalDateTime(row.createdOn?.toString(), DateTimeFormat.DateTimeShort);
          },
        },
      },
      {
        name: 'modifiedOn',
        label: 'Modified On',
        options: {
          sort: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.bulk.reportingParametersList?.results[dataIndex];
            if (!row) return '';
            return getLocalDateTime(row.modifiedOn?.toString(), DateTimeFormat.DateTimeShort);
          },
        },
      },
      {
        name: '',
        label: '',
        options: {
          sort: false,
          filter: false,
          viewColumns: false,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.bulk.reportingParametersList?.results[dataIndex];
            if (!row) return null;

            return (
              <Box display="flex">
                <Tooltip title="Edit">
                  <span>
                    <IconButton color="primary" disabled={row.canEdit === false} onClick={() => history.push(`/parameters/edit/${row.id}`)}>
                      <Edit />
                    </IconButton>
                  </span>
                </Tooltip>

                {props.hasClientEditAdminOnlyPermission && (
                  <Tooltip title={row.isSystemShared ? 'Disable system template' : 'Enable system template'}>
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          props
                            .setReportingParameterSystemShare({
                              reportParameterId: row.id,
                              systemShared: !row.isSystemShared,
                            })
                            .then(() => {
                              fetchData();
                            });
                        }}
                      >
                        {row.isSystemShared ? <ArrowDownward /> : <ArrowUpward />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {row.createdById == props.userId && (
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setClickedRow(row);
                          setClickedState('delete');
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
      },
    ],
    [props.bulk.reportingParametersList, props.hasClientEditAdminOnlyPermission, props.userId]
  );

  return (
    <>
      <Paper elevation={4}>
        <ServerSideDataTable<GetReportParametersViewModel>
          pagedRequest={pagedRequest}
          handlePagedRequest={setPagedRequest}
          pagedResult={props.bulk.reportingParametersList}
          loadingProgress={props.fetchReportingParametersProgress}
          columns={columns}
          options={tableOptions}
        />
      </Paper>
      {clickedRow && clickedState === 'delete' && (
        <ReportingParametersDeleteModal
          handleDelete={() =>
            props.deleteReportingParameter(clickedRow.id).then(() => {
              fetchData();
            })
          }
          row={clickedRow}
          isOpen={true}
          handleClose={closeModal}
        />
      )}
    </>
  );
};
